import type im from "immutable";

import { isFilterable } from "~/components/inspectorv2/traverseValue/TypeTagInfo";
import { joinPath } from "~/components/inspectorv2/traverseValue/ValuePath";

import {
	type WherePredicateOperationName,
	type WherePredicateOperations,
	wherePredicateOperations,
} from "./Operation";
import { type QueryBuilderState } from "./QueryBuilderState";
import { type QueryableField } from "./QueryableField";

/**
 * A `Where` node in a query.
 */
export type Where = im.List<WherePredicate>;

export const appendWherePredicate = (
	state: QueryBuilderState | null,
	field?: QueryableField,
	arg?: string
): QueryBuilderState | null => {
	if (!state) {
		return state;
	}

	let filterableField = field;

	if (!filterableField) {
		const first = state.get("fields").find((f) => isFilterable(f.typeInfo));
		if (!first) {
			return state;
		}

		filterableField = first;
	}

	const predicate = makePredicate(filterableField, arg);
	if (!predicate) {
		return state;
	}

	const preds = state.get("wherePredicates").push(predicate);
	return state.set("wherePredicates", preds);
};

export const replaceFieldInWherePredicate = (
	state: QueryBuilderState | null,
	index: number,
	field: QueryableField
): QueryBuilderState | null => {
	if (!state) {
		return state;
	}

	const predicate = state.get("wherePredicates").get(index);
	if (!predicate) {
		return state;
	}

	const newPredicate = makePredicate(field, undefined);
	if (!newPredicate) {
		return state;
	}

	const preds = state.get("wherePredicates").set(index, newPredicate);
	return state.set("wherePredicates", preds);
};

export const replaceOperationInWherePredicate = (
	state: QueryBuilderState | null,
	index: number,
	op: WherePredicateOperations
): QueryBuilderState | null => {
	if (!state) {
		return state;
	}

	const predicate = state.get("wherePredicates").get(index);
	if (!predicate) {
		return state;
	}

	const newPredicate = Object.assign({}, predicate);
	newPredicate.operation = op;

	const preds = state.get("wherePredicates").set(index, newPredicate);
	return state.set("wherePredicates", preds);
};

export const replaceArgInWherePredicate = (
	state: QueryBuilderState | null,
	index: number,
	arg: string
): QueryBuilderState | null => {
	if (!state) {
		return state;
	}

	const predicate = state.get("wherePredicates").get(index);
	if (!predicate) {
		return state;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const newPredicate = Object.assign({}, predicate);
	newPredicate.arg = arg;

	const preds = state.get("wherePredicates").set(index, newPredicate);
	return state.set("wherePredicates", preds);
};

export const deleteWherePredicate = (
	s: QueryBuilderState | null,
	i: number
): QueryBuilderState | null => {
	if (!s) {
		return s;
	}

	return s.set("wherePredicates", s.get("wherePredicates").delete(i));
};

/**
 * The set of operations availble to a filter predicate for some type. For example, a field might be
 * a `string`, in which case the filter predicate would be able to filter on properties like
 * substring, suffix, prefix, etc.
 */
export type OperationType<T extends WherePredicateOperationName> =
	(typeof wherePredicateOperations)[T][number];

/**
 * All possible filter predicates for a `where` query node.
 */
export type AllWherePredicates = {
	[T in WherePredicateOperationName]: {
		field: string;
		fieldType: T;
		operation: OperationType<T>;
		arg?: string;
	};
};

/**
 * A predicate for a `where` query node.
 */
export type WherePredicate = AllWherePredicates[WherePredicateOperationName];

const makePredicate = (field: QueryableField, arg?: string): WherePredicate | undefined => {
	const [fieldName, fieldType] = [joinPath(field.path), field.typeInfo.typeTag];

	switch (fieldType) {
		case "boolean":
		case "number":
		case "bigint":
		case "string": {
			const ops = wherePredicateOperations[fieldType];
			const predicate: AllWherePredicates[typeof fieldType] = {
				field: fieldName,
				fieldType,
				// FIXME
				//
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				operation: ops[0] as any,
				arg,
			};

			return predicate;
		}
	}
};
